import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import PageHeader from '../../components/UI/PageHeader';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import FormSelect from '../../components/Forms/FormSelect';
import Pagination from '../../components/UI/Pagination';
import { getUserPayments } from '../../services/payments/paymentsService';
import { getUserById } from '../../services/users/usersService';
import { PaymentDto, PaymentStatus } from '../../services/payments/types';
import { UserDto } from '../../services/users/types';

const UserPaymentsList: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const auth = useAuth();
  
  const [payments, setPayments] = useState<(PaymentDto & { charge?: any })[]>([]);
  const [user, setUser] = useState<UserDto | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const pageSize = 10;

  // Extrair parâmetros da URL
  const queryParams = new URLSearchParams(location.search);
  const pageParam = queryParams.get('page');
  const statusParam = queryParams.get('status');

  // Inicializar estado com valores da URL, se existirem
  useEffect(() => {
    if (pageParam) {
      setCurrentPage(parseInt(pageParam));
    }
    if (statusParam) {
      setStatusFilter(statusParam);
    }
  }, [pageParam, statusParam]);

  // Buscar dados do usuário
  const fetchUserData = useCallback(async () => {
    if (!auth.isAuthenticated || !id) return;
    
    try {
      const userData = await getUserById(auth.user || null, id);
      setUser(userData);
    } catch (err) {
      console.error('Error fetching user data:', err);
      setError('Failed to fetch user details.');
    }
  }, [auth.isAuthenticated, auth.user, id]);

  // Buscar pagamentos do usuário
  const fetchPayments = useCallback(async () => {
    if (!auth.isAuthenticated) {
      setError('You must be authenticated to view payments.');
      return;
    }
    
    if (!id) {
      setError('User ID is missing.');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await getUserPayments(auth.user || null, id, currentPage, pageSize);
      
      // Filtrar por status se necessário
      let filteredPayments = response.content;
      if (statusFilter) {
        filteredPayments = response.content.filter(
          payment => payment.payment_status === statusFilter
        );
      }
      
      setPayments(filteredPayments);
      
      // Calcular total de páginas
      const totalElements = statusFilter 
        ? filteredPayments.length // Se filtrado, usar comprimento da lista filtrada
        : response.total_elements;
        
      setTotalPages(Math.max(1, Math.ceil(totalElements / pageSize)));
    } catch (err) {
      console.error('Error fetching payments:', err);
      setError('Failed to fetch payments. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [auth.isAuthenticated, auth.user, id, currentPage, pageSize, statusFilter]);
  
  // Efeito para buscar dados quando componente montar ou parâmetros mudarem
  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);
  
  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);
  
  // Atualizar URL com filtros
  const updateQueryParams = useCallback(() => {
    const params = new URLSearchParams();
    
    if (currentPage > 1) {
      params.set('page', currentPage.toString());
    }
    
    if (statusFilter) {
      params.set('status', statusFilter);
    }
    
    navigate({
      pathname: location.pathname,
      search: params.toString()
    }, { replace: true });
  }, [currentPage, statusFilter, navigate, location.pathname]);
  
  useEffect(() => {
    updateQueryParams();
  }, [currentPage, statusFilter, updateQueryParams]);
  
  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setStatusFilter(e.target.value);
    setCurrentPage(1); // Reset para primeira página ao mudar filtro
  };
  
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };
  
  const handleCreateCharge = () => {
    // Navigate to the charge creation page with the user ID as a query parameter
    navigate(`/payments/create?user_id=${id}`);
  };
  
  // Formatar valores monetários
  const formatCurrency = (amount?: number, currency: string = 'BRL') => {
    if (amount === undefined) return 'N/A';
    
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: currency || 'BRL'
    }).format(amount / 100); // Assumindo que o valor está em centavos
  };
  
  // Formatar data para o formato local
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };
  
  // Obter classes de CSS para o status do pagamento
  const getPaymentStatusBadgeClasses = (status?: PaymentStatus) => {
    switch (status) {
      case PaymentStatus.APPROVED:
      case PaymentStatus.CAPTURED:
        return 'bg-green-100 text-green-700';
      case PaymentStatus.PENDING:
        return 'bg-yellow-100 text-yellow-700';
      case PaymentStatus.CANCELLED:
      case PaymentStatus.EXPIRED:
      case PaymentStatus.FAILED:
        return 'bg-red-100 text-red-700';
      case PaymentStatus.REFUNDED:
        return 'bg-purple-100 text-purple-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };
  
  return (
    <div>
      <PageHeader
        title={user ? `Payments for ${user.first_name} ${user.last_name || ''}` : 'User Payments'}
        breadcrumbs={[
          { label: "Dashboard", to: "/" },
          { label: "Users", to: "/users" },
          { label: user?.username || id || 'User', to: `/users/${id}` },
          { label: "Payments" }
        ]}
        actions={
          <div className="flex space-x-3">
            <Button
              variant="primary"
              onClick={handleCreateCharge}
            >
              <i className="fas fa-plus mr-2"></i>
              Create Charge
            </Button>
            <Button
              variant="secondary"
              onClick={() => navigate(`/users/${id}`)}
            >
              <i className="fas fa-arrow-left mr-2"></i>
              Back to User
            </Button>
          </div>
        }
      />
      
      {error && (
        <div className="bg-red-50 text-red-700 p-4 rounded-md mb-4">
          {error}
          <Button 
            variant="ghost" 
            size="sm" 
            className="ml-2"
            onClick={fetchPayments}
          >
            Retry
          </Button>
        </div>
      )}
      
      <Card className="mb-6">
        <div className="p-6">
          <FormSelect
            label="Payment Status"
            id="statusFilter"
            value={statusFilter}
            onChange={handleStatusChange}
            leftIcon={<i className="fas fa-filter text-gray-400"></i>}
            options={[
              { value: "", label: "All Statuses" },
              { value: PaymentStatus.PENDING, label: "Pending" },
              { value: PaymentStatus.APPROVED, label: "Approved" },
              { value: PaymentStatus.CAPTURED, label: "Captured" },
              { value: PaymentStatus.CANCELLED, label: "Cancelled" },
              { value: PaymentStatus.EXPIRED, label: "Expired" },
              { value: PaymentStatus.FAILED, label: "Failed" },
              { value: PaymentStatus.REFUNDED, label: "Refunded" },
            ]}
            className="w-full md:w-64"
          />
        </div>
      </Card>
      
      <Card>
        {isLoading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : payments.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500 mb-4">
              No payments found
              {statusFilter && ` with status: ${statusFilter}`}.
            </p>
            <Button 
              variant="primary"
              onClick={handleCreateCharge}
              className="mt-2"
            >
              <i className="fas fa-plus mr-2"></i>
              Create a Charge
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                    ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                    Charge Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                    Gateway
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                    Method
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                    Paid At
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-900 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {payments.map((payment) => (
                  <tr key={payment.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {payment.id?.substring(0, 8)}...
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {payment.charge?.description || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {payment.charge?.amount ? formatCurrency(payment.charge.amount, payment.charge.currency) : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {payment.payment_gateway || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {payment.payment_method || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getPaymentStatusBadgeClasses(payment.payment_status)}`}>
                        {payment.payment_status || 'Unknown'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {payment.paid_at ? formatDate(payment.paid_at) : 'Not paid yet'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        {payment.charge?.id && (
                          <Link to={`/payments/charges/${payment.charge.id}`}>
                            <Button variant="ghost" size="xs" title="View charge details">
                              <i className="fas fa-receipt"></i>
                            </Button>
                          </Link>
                        )}
                        {payment.receipt_url && (
                          <a href={payment.receipt_url} target="_blank" rel="noopener noreferrer">
                            <Button variant="ghost" size="xs" title="View receipt">
                              <i className="fas fa-file-invoice"></i>
                            </Button>
                          </a>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        {payments.length > 0 && totalPages > 1 && (
          <div className="py-3 px-6 border-t border-gray-200">
            <Pagination
              currentPage={currentPage}
              totalItems={payments.length * totalPages}
              pageSize={pageSize}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </Card>
    </div>
  );
};

export default UserPaymentsList;